(() => {
  const MAX_SCALE = 3;
  const MIN_SCALE = 1;
  const DOUBLE_TAP_DELAY = 280;
  const GALLERY_IMAGE_SELECTOR = '.product__media-list .product__media img, .prd-ProductMediaModal_Medias-image img';
  const RESET_TRIGGER_SELECTOR =
    '.thumbnail-list__item, .flickity-button, [data-el="media-gallery.previous"], [data-el="media-gallery.next"], [data-el="media-gallery.grid-item"], [data-el="media-gallery.close-grid"], [data-el="media-gallery.close-slider"]';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const distance = (touchA, touchB) => Math.hypot(touchA.clientX - touchB.clientX, touchA.clientY - touchB.clientY);

  class MobileZoomImage {
    constructor(image) {
      this.image = image;
      this.mediaItem = image.closest('.product__media-item');
      this.zoomContainer = image.closest('.product__media-item, .prd-ProductMediaModal_Medias-image');
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.lastTapTime = 0;
      this.lastTapX = 0;
      this.lastTapY = 0;
      this.lastTouchX = 0;
      this.lastTouchY = 0;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchStartTime = 0;
      this.hasTouchMoved = false;
      this.startDistance = 0;
      this.startScale = 1;
      this.isPanning = false;
      this.listeners = [];

      this.image.classList.add('prd-MobileZoom_Target');
      this.addListener(this.image, 'touchstart', this.onTouchStart.bind(this), { passive: false, capture: true });
      this.addListener(this.image, 'touchmove', this.onTouchMove.bind(this), { passive: false, capture: true });
      this.addListener(this.image, 'touchend', this.onTouchEnd.bind(this), { capture: true });
      this.addListener(this.image, 'touchcancel', this.onTouchCancel.bind(this), { capture: true });
      if (!('ontouchstart' in window)) {
        this.addListener(this.image, 'dblclick', this.onDoubleClick.bind(this));
      }
    }

    addListener(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.listeners.push({ target, type, handler, options });
    }

    destroy() {
      for (const listener of this.listeners) {
        listener.target.removeEventListener(listener.type, listener.handler, listener.options);
      }

      this.listeners = [];
      this.reset();
      this.image.classList.remove('prd-MobileZoom_Target');
    }

    setZoomStateClass() {
      const isZoomed = this.scale > MIN_SCALE;
      this.image.classList.toggle('is-mobile-zoomed', isZoomed);
      this.mediaItem?.classList.toggle('is-mobile-zoomed', isZoomed);
      this.zoomContainer?.classList.toggle('is-mobile-zoomed', isZoomed);
      this.setSliderPaused(isZoomed);
    }

    setSliderPaused(shouldPause) {
      if (!window.Flickity || typeof window.Flickity.data !== 'function') return;

      const flickityTrack = this.image.closest('.flickity-enabled');
      if (!flickityTrack) return;

      const flickityInstance = window.Flickity.data(flickityTrack);
      if (!flickityInstance || !flickityInstance.options) return;

      const shouldEnableDrag = !shouldPause;
      if (flickityInstance.options.draggable === shouldEnableDrag) return;

      try {
        flickityInstance.options.draggable = shouldEnableDrag;
        if (typeof flickityInstance.updateDraggable === 'function') {
          flickityInstance.updateDraggable();
        }
      } catch (_error) {
        // Keep zoom interaction working even if slider state can't update.
      }
    }

    clampTranslate() {
      const width = this.image.clientWidth;
      const height = this.image.clientHeight;
      const maxX = Math.max(0, ((width * this.scale) - width) / 2);
      const maxY = Math.max(0, ((height * this.scale) - height) / 2);

      this.translateX = clamp(this.translateX, -maxX, maxX);
      this.translateY = clamp(this.translateY, -maxY, maxY);
    }

    applyTransform() {
      this.clampTranslate();
      this.image.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
      this.setZoomStateClass();
    }

    reset() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.isPanning = false;
      this.image.style.transform = '';
      this.setZoomStateClass();
    }

    onTouchStart(event) {
      if (event.touches.length === 2) {
        this.startDistance = distance(event.touches[0], event.touches[1]);
        this.startScale = this.scale;
        this.isPanning = false;
        event.preventDefault();
        return;
      }

      if (event.touches.length !== 1) return;

      const touch = event.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchStartTime = Date.now();
      this.hasTouchMoved = false;
      this.lastTouchX = touch.clientX;
      this.lastTouchY = touch.clientY;
      this.isPanning = this.scale > MIN_SCALE;

      if (this.scale > MIN_SCALE) {
        event.preventDefault();
      }
    }

    onTouchMove(event) {
      if (event.touches.length === 2) {
        const currentDistance = distance(event.touches[0], event.touches[1]);
        if (!this.startDistance) return;

        this.scale = clamp((currentDistance / this.startDistance) * this.startScale, MIN_SCALE, MAX_SCALE);
        this.applyTransform();
        event.preventDefault();
        return;
      }

      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (Math.abs(touch.clientX - this.touchStartX) > 10 || Math.abs(touch.clientY - this.touchStartY) > 10) {
        this.hasTouchMoved = true;
      }
      if (!this.isPanning) return;

      const deltaX = touch.clientX - this.lastTouchX;
      const deltaY = touch.clientY - this.lastTouchY;
      this.lastTouchX = touch.clientX;
      this.lastTouchY = touch.clientY;
      this.translateX += deltaX;
      this.translateY += deltaY;
      this.applyTransform();
      event.preventDefault();
    }

    onTouchEnd(event) {
      if (event.touches.length > 0) return;

      const tapDuration = Date.now() - this.touchStartTime;
      const isTap = !this.hasTouchMoved && tapDuration < 260;

      if (this.scale < 1.05 && !isTap) {
        this.reset();
      }

      if (!isTap) return;

      const now = Date.now();
      const tapDistance = Math.hypot(this.touchStartX - this.lastTapX, this.touchStartY - this.lastTapY);
      const isDoubleTap = now - this.lastTapTime <= DOUBLE_TAP_DELAY && tapDistance <= 28;
      this.lastTapTime = now;
      this.lastTapX = this.touchStartX;
      this.lastTapY = this.touchStartY;

      if (!isDoubleTap) return;
      this.toggleZoom();
    }

    onTouchCancel() {
      if (this.scale <= MIN_SCALE) {
        this.reset();
      }
    }

    onDoubleClick(event) {
      event.preventDefault();
      this.toggleZoom();
    }

    toggleZoom() {
      if (this.scale > MIN_SCALE) {
        this.reset();
        return;
      }

      this.scale = 2;
      this.translateX = 0;
      this.translateY = 0;
      this.applyTransform();
    }
  }

  const instances = new Map();
  let observer;
  let domObserver;
  let globalListenersBound = false;

  const isMobileViewport = () => window.matchMedia('(max-width: 900px)').matches;
  const shouldEnable = () => isMobileViewport();

  const isActiveMediaItem = (mediaItem) => {
    if (!mediaItem) return false;
    if (mediaItem.classList.contains('is-selected')) return true;
    if (mediaItem.classList.contains('is-active')) return true;
    return mediaItem.getAttribute('aria-current') === 'true';
  };

  const resetInactiveItems = () => {
    for (const instance of instances.values()) {
      if (isActiveMediaItem(instance.mediaItem)) continue;
      instance.reset();
    }
  };

  const bindGlobalListeners = () => {
    if (globalListenersBound) return;

    document.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest(RESET_TRIGGER_SELECTOR)) return;
        resetInactiveItems();
      },
      true
    );

    window.addEventListener('resize', init);
    globalListenersBound = true;
  };

  const bindObserver = () => {
    if (observer) return;

    observer = new MutationObserver(() => {
      resetInactiveItems();
    });

    const mediaLists = document.querySelectorAll('.product__media-list, .prd-ProductMediaModal_Items');
    for (const mediaList of mediaLists) {
      observer.observe(mediaList, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-current'],
      });
    }
  };

  const bindDomObserver = () => {
    if (domObserver) return;

    const scheduleInit = (() => {
      let scheduled = false;

      return () => {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(() => {
          scheduled = false;
          init();
        });
      };
    })();

    domObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) continue;

        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches?.(GALLERY_IMAGE_SELECTOR) || node.querySelector?.(GALLERY_IMAGE_SELECTOR)) {
            scheduleInit();
            return;
          }
        }
      }
    });

    domObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  const destroyAll = () => {
    for (const [image, instance] of instances.entries()) {
      instance.destroy();
      instances.delete(image);
    }
  };

  const init = () => {
    if (!shouldEnable()) {
      destroyAll();
      return;
    }

    bindGlobalListeners();
    bindObserver();
    bindDomObserver();

    const images = document.querySelectorAll(GALLERY_IMAGE_SELECTOR);
    for (const image of images) {
      if (!(image instanceof HTMLImageElement)) continue;
      if (instances.has(image)) continue;
      instances.set(image, new MobileZoomImage(image));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();
